



































































































































import Vue from 'vue'
import { Button } from 'element-ui'
import fb from '@/firebase'

import { AbilityAction, AbilitySubject } from '@/abilitiesByRole'
import { Team } from '@/types/teams'
import Modal from '@/components/Modal'
import KisweInput from '@/components/Kiswe/KisweInput'
import ClipLoader from '@/components/vue-spinner/ClipLoader.vue'

import './StreamAuthSettings.scss'
import { Feature } from '@/featureFlags/featureFlags'
import { minLength, required, sameAs } from 'vuelidate/lib/validators'
import { AdminDocNames, CollectionNames } from '@/types'

export default Vue.extend({
  name: 'StreamAuthSettings',
  data: () => ({
    rtmpEditModalShown: false,
    showFailedMessage: false,
    isProcessing: false,
    editCredentials: {
      login: '',
      password: '',
      confirmPassword: ''
    },
    rtmpAuthErrorMessage: ''
  }),
  computed: {
    canDoOps (): boolean {
      return this.$can(AbilityAction.Do, AbilitySubject.Ops)
    },
    canSeeStreamAuth (): boolean {
      return this.$can(AbilityAction.Read, AbilitySubject.StreamAuth) && this.$hasFeature(Feature.STREAM_AUTHENTICATION)
    },
    canEditStreamAuth (): boolean {
      return this.$can(AbilityAction.Edit, AbilitySubject.StreamAuth) && this.$hasFeature(Feature.STREAM_AUTHENTICATION)
    },
    allowDeleteRtmp (): boolean {
      return this.hasRtmpAuth && !this.rtmpAuthUpdatePending
    },
    team (): Team | undefined {
      return this.$store.direct.state.team.team
    },
    rtmpAuthUpdatePending (): boolean {
      return this.isProcessing || (this.$store.direct.state.team.team?.streams_auth?.rtmp?.pending ?? false)
    },
    hasRtmpAuth (): boolean {
      const rtmpAuthInfo = this.$store.direct.state.team.team?.streams_auth?.rtmp
      // Must have rtmp object and a non-empty login
      return rtmpAuthInfo !== undefined && !!rtmpAuthInfo.login
    },
    rtmpAuthLogin (): string {
      return this.$store.direct.state.team.team?.streams_auth?.rtmp?.login ?? ''
    },
    rtmpAuthPending (): boolean {
      return this.$store.direct.state.team.team?.streams_auth?.rtmp?.pending ?? false
    },
    rtmpAuthError (): string|undefined {
      return this.$store.direct.state.team.team?.streams_auth?.rtmp?.error;
    }
  },
  watch: {
    rtmpAuthPending (newVal: boolean): void {
      if (newVal === false && this.isProcessing) {
        console.log('Pending changed to false while isProcessing, updating the latter to false')
        this.isProcessing = false
        // The local rtmpAuthErrorMessage is cleared when submitting the request for change, but if the changes fail
        // again with the same error, then the watch on `rtmpAuthError` won't trigger.
        this.updateRtmpErrorMessage(this.rtmpAuthError)
      }
    },
    rtmpAuthLogin (newVal: string): void {
      if (!this.rtmpEditModalShown) this.editCredentials.login = newVal
    },
    rtmpAuthError (newVal: string|undefined): void {
      this.updateRtmpErrorMessage(newVal)
    }
  },
  mounted () {
    // FIXME: The rtmpAuthLogin watch doesn't seem to trigger when the page gets initially loaded. Which results in
    // the login staying empty. So force it to update here when being mounted.
    this.editCredentials.login = this.rtmpAuthLogin
    this.updateRtmpErrorMessage(this.rtmpAuthError)
  },
  methods: {
    clearEditPassword (): void {
      this.editCredentials.password = ''
      this.editCredentials.confirmPassword = ''
      this.$v.$reset()
    },
    showRtmpEdit (): void {
      const showModal = () => {
        this.editCredentials.login = this.rtmpAuthLogin
        this.clearEditPassword()
        this.rtmpEditModalShown = true
      }
      if (this.hasRtmpAuth) {
        this.$confirm(
          'Changing the RTMP authentication information while authenticated input streams are active, will cause those input streams to fail.',
          'Warning',
          {
            confirmButtonText: 'Continue',
            cancelButtonText: 'Cancel',
            type: 'warning'
          }
        ).then(showModal).catch(() => {})
      } else {
        showModal()
      }
    },
    cancelRtmpEdit (): void {
      this.hideRtmpEdit()
      // Update login in case an update was received while the edit modal was shown, and the user canceled the edit.
      this.editCredentials.login = this.rtmpAuthLogin
    },
    hideRtmpEdit (): void {
      this.clearEditPassword()
      this.rtmpEditModalShown = false
    },
    deleteRtmpAuth (): void {
      this.$msgbox({
        message: this.$createElement('p', undefined, [
          this.$createElement('p', undefined, 'Are you sure you want remove the RTMP authentication information?'),
          this.$createElement('p', undefined, 'This will prevent the use of any RTMP input streams that have authentication enabled.')
        ]),
        title: 'Warning',
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        showCancelButton: true,
        type: 'warning'
      }).then(async () => {
        const errMsg = 'Error sending authentication deletion request'
        const teamId = this.team?.id
        if (teamId === undefined) {
          this.$message.error('Deleting RTMP authentication failed')
          console.error("No team id available. Aborting credentials deletion.")
          this.rtmpAuthErrorMessage = errMsg
          return
        }

        this.isProcessing = true
        this.rtmpAuthErrorMessage = ''

        this.$store.direct.dispatch.logs.addLog({ message: `Requested RTMP authentication to be removed for team: '${this.team?.id}'`, level: 'info' })
        // TODO: Does this belong in team.actions.ts?
        try {
          await fb.db.collection(CollectionNames.ADMIN).doc(AdminDocNames.STREAMS_AUTH).set({
            [teamId]: {
              rtmp: {
                // Make sure if there was any previous credetial assignment
                // still waiting to process, that we cancel that.
                login: fb.firebase.firestore.FieldValue.delete(),
                password: fb.firebase.firestore.FieldValue.delete(),
                deleted: true
              }
            }
          }, { merge: true })
          const fsPath = new fb.firebase.firestore.FieldPath('streams_auth', 'rtmp', 'pending')
          await fb.db.collection(CollectionNames.TEAMS).doc(teamId).update(fsPath, true)
        } catch (error) {
          console.error('Failed to delete RTMP credentials:', error)
          this.$store.direct.dispatch.logs.addLog({ message: `Deleting RTMP authentication failed for team: '${this.team?.id}'`, level: 'info' })
          this.$message.error('Deleting RTMP authentication failed')
          this.rtmpAuthErrorMessage = errMsg
          this.isProcessing = false
        }
      }).catch(() => {
      })
    },
    async updateRtmpCredentials (): Promise<void> {
      this.$v.$touch()
      if (this.$v.$invalid) return

      const errMsg = 'Error sending authentication change request'
      const teamId = this.team?.id
      if (teamId === undefined) {
        this.$message.error('Setting RTMP authentication failed')
        console.error("No team id available. Aborting credentials update.")
        this.rtmpAuthErrorMessage = errMsg
        return
      }

      this.isProcessing = true
      this.rtmpAuthErrorMessage = ''

      this.$store.direct.dispatch.logs.addLog({ message: `Requested RTMP authentication with login: '${this.editCredentials.login}' to be added for team: '${this.team?.id}'`, level: 'info' })
      // TODO: Does this belong in team.actions.ts?
      try {
        await fb.db.collection(CollectionNames.ADMIN).doc(AdminDocNames.STREAMS_AUTH).set({
          [teamId]: {
            rtmp: {
              login: this.editCredentials.login,
              password: this.editCredentials.password,
              // Make sure if there was any previous delete still waiting to
              // process, that we cancel that.
              deleted: fb.firebase.firestore.FieldValue.delete()
            }
          }
        }, { merge: true })
        const fsPath = new fb.firebase.firestore.FieldPath('streams_auth', 'rtmp', 'pending')
        await fb.db.collection(CollectionNames.TEAMS).doc(teamId).update(fsPath, true)
      } catch (error) {
        console.error('Failed to create RTMP credentials:', error)
        this.$store.direct.dispatch.logs.addLog({ message: `Creating RTMP authentication failed for team: '${this.team?.id}'`, level: 'info' })
        this.$message.error('Setting RTMP authentication failed')
        this.rtmpAuthErrorMessage = errMsg
        // Reset login when failing to request the auth changes.
        this.editCredentials.login = this.rtmpAuthLogin
        this.isProcessing = false
      }
      this.hideRtmpEdit()
    },
    updateRtmpErrorMessage (errMsg: string|undefined): void {
      if (errMsg === undefined) {
        this.rtmpAuthErrorMessage = ''
      } else {
        // If we were trying to update the auth details and it failed, make sure to show the old login again and not
        // any new value that was provided in the edit dialog.
        this.editCredentials.login = this.rtmpAuthLogin
        if (this.canDoOps) {
          this.rtmpAuthErrorMessage = errMsg
        } else {
          this.rtmpAuthErrorMessage = 'Error while processing authentication changes'
        }
      }
    }
  },
  validations: {
    editCredentials: {
      login: {
        required,
        minLength: minLength(2)
      },
      password: {
        required,
        minLength: minLength(6)
      },
      confirmPassword: {
        sameAsPassword: sameAs('password')
      }
    }
  },
  components: {
    KisweInput,
    Modal,
    'clip-loader': ClipLoader,
    'el-button': Button
  }
})
